import React from 'react';
import { Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

/** Longest edge of the thumb bitmap (padding sits outside this). */
export const PDF_PAGE_THUMB_IMAGE_MAX_EDGE_PX = 154;
/** Card inset around the image (same on every side) — selection chrome. */
export const PDF_PAGE_THUMB_CARD_INSET_PX = 16;

type PdfImportPageThumbnailProps = {
  imageDataUrl: string;
  label: string;
  /** Preview / focus selection in the step-1 rail. */
  focused?: boolean;
  /** Import-ticked in the step-1 rail. */
  selected?: boolean;
  /**
   * Selection/hover chrome padding around the bitmap.
   * Off for static step-2 thumbs (no select / hover).
   */
  selectionChrome?: boolean;
  onImageClick?: () => void;
  onLabelClick?: () => void;
  className?: string;
  cardClassName?: string;
  /** Ref to the page bitmap — used for step 2 → 3 zoom. */
  imageRef?: React.Ref<HTMLImageElement>;
  /** Fires when the bitmap finishes loading (layout that depends on size). */
  onImageLoad?: (event: React.SyntheticEvent<HTMLImageElement>) => void;
  /**
   * Content beside the page card (e.g. checkbox), vertically centered on the
   * image card — not the label row.
   */
  leading?: React.ReactNode;
  /** Width of {@link leading} column when provided. */
  leadingWidthPx?: number;
  /** Gap between {@link leading} and the page card. */
  leadingGapPx?: number;
};

/**
 * Shared PDF page thumbnail used by import wizard steps 1 and 2.
 */
export const PdfImportPageThumbnail: React.FC<PdfImportPageThumbnailProps> = ({
  imageDataUrl,
  label,
  focused = false,
  selected = false,
  selectionChrome = true,
  onImageClick,
  onLabelClick,
  className,
  cardClassName,
  imageRef,
  onImageLoad,
  leading,
  leadingWidthPx = 32,
  leadingGapPx = 4,
}) => {
  const { classes, cx } = useStyles({
    hasLeading: leading != null,
    leadingWidthPx,
    leadingGapPx,
    selectionChrome,
  });
  const interactive = Boolean(onImageClick) && selectionChrome;

  const image = (
    <span className={classes.imageFrame}>
      <img
        ref={imageRef}
        src={imageDataUrl}
        alt={label}
        className={classes.image}
        onLoad={onImageLoad}
      />
    </span>
  );

  return (
    <div className={cx(classes.root, className)}>
      {leading != null && <div className={classes.leading}>{leading}</div>}
      <div
        className={cx(
          classes.card,
          selectionChrome && selected && classes.cardSelected,
          selectionChrome && focused && classes.cardFocused,
          cardClassName,
        )}
      >
        {interactive ? (
          <button
            type="button"
            className={classes.imageButton}
            onClick={onImageClick}
          >
            {image}
          </button>
        ) : (
          image
        )}
      </div>
      <Typography
        className={cx(
          classes.label,
          selectionChrome && selected && classes.labelSelected,
          selectionChrome && focused && classes.labelFocused,
          (!onLabelClick || !selectionChrome) && classes.labelStatic,
        )}
        title={label}
        onClick={selectionChrome ? onLabelClick : undefined}
      >
        {label}
      </Typography>
    </div>
  );
};

type StyleParams = {
  hasLeading: boolean;
  leadingWidthPx: number;
  leadingGapPx: number;
  selectionChrome: boolean;
};

const useStyles = makeStyles<StyleParams>()(
  (theme, { hasLeading, leadingWidthPx, leadingGapPx, selectionChrome }) => ({
    root: {
      display: hasLeading ? 'grid' : 'flex',
      ...(hasLeading
        ? {
            gridTemplateColumns: `${leadingWidthPx}px auto`,
            gridTemplateRows: 'auto auto',
            columnGap: leadingGapPx,
            rowGap: 2,
            alignItems: 'start',
          }
        : {
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 2,
          }),
      // Hug the selection chrome — avoid side dead space around the card.
      width: 'fit-content',
      maxWidth: '100%',
      minWidth: 0,
    },
    leading: {
      gridColumn: 1,
      gridRow: 1,
      // Center on the card row (image + equal inset) — not the label.
      alignSelf: 'center',
      justifySelf: 'center',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: leadingWidthPx,
      minHeight: 0,
    },
    card: {
      ...(hasLeading
        ? {
            gridColumn: 2,
            gridRow: 1,
          }
        : {}),
      // Shrink-wrap the bitmap so selection chrome has equal inset on all sides.
      display: 'inline-flex',
      flexDirection: 'column',
      flex: 'none',
      width: 'fit-content',
      maxWidth: '100%',
      padding: selectionChrome ? PDF_PAGE_THUMB_CARD_INSET_PX : 0,
      border: 'none',
      borderRadius: 6,
      backgroundColor: 'transparent',
      boxSizing: 'border-box',
      pointerEvents: selectionChrome ? 'auto' : 'none',
    },
    cardSelected: {
      backgroundColor: theme.palette.grey[200],
    },
    cardFocused: {
      backgroundColor: '#E2F2FF',
    },
    imageButton: {
      display: 'block',
      margin: 0,
      padding: 0,
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      lineHeight: 0,
    },
    imageFrame: {
      display: 'block',
      lineHeight: 0,
      maxWidth: '100%',
    },
    image: {
      display: 'block',
      maxWidth: PDF_PAGE_THUMB_IMAGE_MAX_EDGE_PX,
      maxHeight: PDF_PAGE_THUMB_IMAGE_MAX_EDGE_PX,
      width: 'auto',
      height: 'auto',
      objectFit: 'contain',
    },
    label: {
      ...(hasLeading
        ? {
            gridColumn: 2,
            gridRow: 2,
          }
        : {}),
      width: '100%',
      fontSize: 11,
      fontWeight: 500,
      fontVariantNumeric: 'tabular-nums',
      color: theme.palette.text.secondary,
      textAlign: 'center',
      lineHeight: 1.25,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      cursor: 'pointer',
    },
    labelStatic: {
      cursor: 'default',
    },
    labelSelected: {
      color: theme.palette.grey[700],
      fontWeight: 600,
    },
    labelFocused: {
      color: '#0B99FF',
      fontWeight: 600,
    },
  }),
);
